import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import { Calendar, CheckCircle, Clock, FileText, User, XCircle } from "lucide-react";
import { formatDateTime, formatDuration } from "@repo/utils";

interface FormReponseEgogramme {
  name: string;
  label: string;
  profil: string;
  response: boolean;
}

interface EgogrammeDonnees {
  type: "form";
  template: "egogramme";
  reponses: Record<string, FormReponseEgogramme>;
}

interface CandidatInfo {
  prenom: string;
  nom: string;
  email: string;
  dateDebut?: number;
  dateFin?: number;
  tempsPasse?: number;
}

interface EgogrammeResultsProps {
  donnees: EgogrammeDonnees;
  candidatInfo: CandidatInfo;
}

export function EgogrammeResults({ donnees, candidatInfo }: EgogrammeResultsProps) {
  if (donnees.type !== "form" || donnees.template !== "egogramme") {
    return <div>Type de données incorrect</div>;
  }

  const reponsesList = Object.entries(donnees.reponses);

  const profilCounts: Record<string, { oui: number; non: number }> = {};
  reponsesList.forEach(([, reponse]) => {
    if (!profilCounts[reponse.profil]) {
      profilCounts[reponse.profil] = { oui: 0, non: 0 };
    }
    if (reponse.response) {
      profilCounts[reponse.profil]!.oui++;
    } else {
      profilCounts[reponse.profil]!.non++;
    }
  });

  const getProfilColor = (profil: string) => {
    const colors: Record<string, string> = {
      "Parent critique": "bg-red-100 text-red-800 border-red-200",
      "Parent nourricier": "bg-pink-100 text-pink-800 border-pink-200",
      Adulte: "bg-blue-100 text-blue-800 border-blue-200",
      "Enfant libre": "bg-green-100 text-green-800 border-green-200",
      "Enfant adapté": "bg-yellow-100 text-yellow-800 border-yellow-200",
    };
    return colors[profil] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 p-4 md:p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">Egogramme</h1>
            <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              {candidatInfo.prenom} {candidatInfo.nom} —{" "}
              {candidatInfo.email}
            </div>
          </div>
          <Badge variant="success">Terminée</Badge>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {candidatInfo.tempsPasse !== undefined && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Temps passé</span>
                <p className="font-medium">
                  {formatDuration(candidatInfo.tempsPasse)}
                </p>
              </div>
            </div>
          )}
          {candidatInfo.dateDebut && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Date de début</span>
                <p className="font-medium">
                  {formatDateTime(candidatInfo.dateDebut)}
                </p>
              </div>
            </div>
          )}
          {candidatInfo.dateFin && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Date de fin</span>
                <p className="font-medium">
                  {formatDateTime(candidatInfo.dateFin)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Card Résultats bruts */}
      <Card className="mb-6 w-full overflow-hidden">
        <CardHeader>
          <CardTitle>Résultats bruts</CardTitle>
          <CardDescription>Données brutes de l'évaluation</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <div className="space-y-6">
            {/* Résumé par profil */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Résumé par profil</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.entries(profilCounts).map(([profil, counts]) => (
                  <div
                    key={profil}
                    className={`p-3 rounded-lg border ${getProfilColor(profil)}`}
                  >
                    <p className="text-sm font-medium">{profil}</p>
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <span className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        {counts.oui}
                      </span>
                      <span className="flex items-center gap-1">
                        <XCircle className="h-3 w-3" />
                        {counts.non}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Liste des questions */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Réponses détaillées ({reponsesList.length} questions)
              </h3>
              <div className="space-y-2">
                {reponsesList.map(([key, reponse]) => (
                  <div
                    key={key}
                    className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium">{reponse.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {reponse.name}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={getProfilColor(reponse.profil)}>
                        {reponse.profil}
                      </Badge>
                      {reponse.response ? (
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="h-5 w-5" />
                          <span className="text-sm font-medium">Oui</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-red-500">
                          <XCircle className="h-5 w-5" />
                          <span className="text-sm font-medium">Non</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
